// SparkRenderer blur options that match the rasterizer our splats were TRAINED
// against. Spread into every `new SparkRenderer(...)` so all splats — avatar,
// hair, environment — are rendered the same way.
//
// THE PROBLEM. sparkjs's splat vertex shader applies TWO screen-space dilations
// and treats their alpha differently:
//
//     a += preBlurAmount;                 // dilation, alpha NOT compensated
//     detOrig = a * d - b * b;            // measured AFTER preBlur
//     a += fullBlurAmount;                // dilation, alpha IS compensated
//     det = a * d - b * b;
//     rgba.a *= sqrt(detOrig / det);
//
// Its defaults are `preBlurAmount 0` / `blurAmount 0.3`, so the whole dilation
// lands in the COMPENSATED slot. The 3DGS rasterizer these assets were fit
// against adds the same +0.3 px low-pass and leaves alpha alone, so spark's
// default is a systematic deviation from training — and the attenuation is
// severe, because it scales with how few pixels a splat covers.
//
// Measured on eyeline V10-C's head branch (2214x1177, fov 45, blurAmount 0.3),
// replaying the shader's own maths over the real splats:
//
//     head on screen   mean alpha kept   splats losing >50%
//     1544 px               0.81                3%
//      772 px               0.61               32%
//      386 px               0.37               75%      <- typical framing
//      193 px               0.17               95%
//
// i.e. at a normal conversational distance the avatar renders at ~37% of the
// alpha it was trained with, and the loss is size-dependent, so it eats fine
// structures (hair, brows, lashes) hardest.
//
// THE FIX, with no shader patching. Move the dilation into the UNCOMPENSATED
// slot: with `blurAmount 0`, `fullBlurAmount` is 0, so `det === detOrig` and
// `sqrt(detOrig/det)` is exactly 1 — while the eigen-decomposition still sees
// `a + 0.3`, the same low-pass training applied. Identical dilation, alpha
// untouched.
//
// NOT `preBlurAmount 0, blurAmount 0`: that would drop the low-pass too, which
// training DOES apply, and sub-pixel splats would alias instead.
//
// This is renderer-wide (SparkRenderer owns both as uniforms, not per-mesh),
// which is correct: the standard 3DGS rasterizer dilates without compensating
// for every asset, not just ours, so environment scans and hair want it too.
//
// CAVEAT: spark's depth-of-field path clamps `fullBlurAmount` to at least
// `blurAmount`, so if `focalDistance`/`apertureAngle` are ever enabled the
// attenuation returns for the DoF term. Both default to 0 (DoF off) and nothing
// here sets them.
export const SPARK_TRAINING_BLUR = Object.freeze({
  preBlurAmount: 0.3,
  blurAmount: 0,
})
